// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// Every IR flag, blocking ones first. A blocking, unresolved flag (e.g. the Nomura
// flag-002) can be cleared here via resolve_reporting_flag so the gate becomes satisfiable.
//
// WHO MAY RESOLVE. Investment, Operations OR an admin. 0003r deliberately left this narrower
// than the gate for one reason: it did not want a non-team admin to clear a blocking flag and
// then supply their own second pair of eyes. Sam's second ruling of 2026-08-17 waived four eyes
// for admins outright, so that reason is gone and migration 0003s widened
// resolve_reporting_flag to match the gate. Measured live after 0003s, it opens with
//   IF NOT (is_investment_team() OR is_operations_team() OR is_admin_or_superadmin(_uid))
// so this tab gates on canSign, the same server answer the RPC applies. Resolving is still not
// free of consequence: an admin who clears a flag here and then signs the gate has their
// signature permanently recorded as four_eyes = 'waived_admin', not 'verified'.

import { useOutletContext } from "react-router-dom";
import { EmptyState } from "@/components/research-hub";
import { FlagRow } from "@/components/investment-reporting";
import { useResolveReportingFlag } from "@/hooks/investment-reporting/useApproveReportingGate";
import type { Flag } from "@/types/ir";
import type { CycleContext } from "./ReportingCycleLayout";

export default function FlagsTab() {
  const { cycle, ir, canSign } = useOutletContext<CycleContext>();
  const resolve = useResolveReportingFlag();

  const flags = ir?.flags ?? [];
  if (flags.length === 0) return <EmptyState title="No flags on this cycle." tone="good" />;

  // blocking + unresolved first, then other unresolved, then resolved
  const ordered = [...flags].sort((a, b) => rank(a) - rank(b));

  const onResolve = (flag: Flag) => {
    if (!cycle.id) return;
    resolve.mutate({ cycleId: cycle.id, flagId: flag.id, note: "Resolved via review shell" });
  };

  return (
    <div className="space-y-3">
      {!canSign && (
        <p className="font-body text-xs text-muted-foreground">
          Flags are read-only for you. Resolving a flag is for the Investment team, the Operations
          team, or an admin.
        </p>
      )}
      {ordered.map((f, i) => (
        <FlagRow
          // id + index, NOT id alone. Flag ids are not unique in practice: a producer that
          // re-derives its own family without displacing the prior copies appends duplicates
          // (cycle ec8e622a carried bm-xcheck-001/002/003 three times each), and a duplicate
          // React key makes row identity unstable across re-renders on top of the warning.
          key={`${f.id}-${i}`}
          flag={f}
          onResolve={canSign ? onResolve : undefined}
          resolving={resolve.isPending && resolve.variables?.flagId === f.id}
        />
      ))}
    </div>
  );
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function rank(f: Flag): number {
  const resolvedBy = String(f.resolved_by ?? "").trim();
  const namedResolver = f.resolved === true && UUID_RE.test(resolvedBy);
  // A flag marked resolved with no identifiable resolver, still carrying gate weight, is the
  // single most urgent thing on this tab: gate leg (ii-b) refuses the signature for EVERY
  // approver while it sits there. It used to sort LAST, behind everything, wearing a green
  // "resolved" badge. It sorts first now.
  if (f.resolved === true && !namedResolver && f.blocks_gate === true) return 0;
  if (f.blocks_gate && !f.resolved) return 1;
  if (!f.resolved) return 2;
  if (!namedResolver) return 3;
  return 4;
}
